/**
 * Phase 64 (Plan 64-05) — Training compliance API client.
 *
 * Frontend transport for the 64-04 endpoints. Mirrors `respect.ts`: typed DTOs
 * that match the backend response schemas verbatim, each GET unwrapping the
 * `{ data }` envelope (matrix also returns `{ data, pagination }`).
 *
 *   GET    /api/trainings/my
 *   GET    /api/admin/training/available-courses
 *   GET    /api/admin/training/compliance?courseSlug=…&page=…&pageSize=…
 *   GET    /api/admin/training/requirements
 *   POST   /api/admin/training/requirements
 *   PUT    /api/admin/training/requirements/:id
 *   DELETE /api/admin/training/requirements/:id
 */
import { apiGet, apiPost, apiPut, apiDelete, API_BASE_URL, getBaseAuthHeaders } from './client';

// ===== Types =====

export type ComplianceStateDTO = 'compliant' | 'not_started' | 'overdue';
export type ParticipationDTO = 'mandatory' | 'optional' | 'not_required';
export type RequirementScopeDTO = 'all' | 'employee_type' | 'role' | 'user';
export type RequirementFrequencyDTO = 'oneoff' | 'annual';
export type RequirementAnchorDTO = 'fixed' | 'relative';

/** One completion-history row (GET /trainings/my → completions[]). */
export interface CompletionDTO {
  passedAt: string;
  score: number | null;
}

/** One row of GET /api/trainings/my. */
export interface TrainingStatusDTO {
  slug: string;
  title: string;
  participation: ParticipationDTO;
  state: ComplianceStateDTO;
  dueSoon: boolean;
  note: string | null;
  dojoUrl: string;
  nextDueAt: string | null;
  completions: CompletionDTO[];
}

/** A course from the persisted catalogue. `active` = present in the newest sync. */
export interface AvailableCourseDTO {
  slug: string;
  title: string;
  url: string;
  active: boolean;
  lastSeenAt: string;
}

/** The catalogue read/sync response: the course list + overall last-sync time. */
export interface CatalogResponse {
  courses: AvailableCourseDTO[];
  lastSyncAt: string | null;
}

/** A training requirement policy row (DB shape; dates as ISO strings). */
export interface RequirementDTO {
  id: number;
  courseSlug: string;
  courseTitle: string;
  mandatory: boolean;
  active: boolean;
  scope: RequirementScopeDTO;
  scopeRef: string | null;
  frequency: RequirementFrequencyDTO;
  anchor: RequirementAnchorDTO;
  fixedDueDate: string | null;
  graceDays: number | null;
  recurMonths: number | null;
  /** Users this requirement never applies to (e.g. robotic/service accounts). */
  excludedUserIds: number[];
  createdAt: string;
  updatedAt: string;
}

/** POST /api/admin/training/requirements body. */
export interface RequirementCreateInput {
  courseSlug: string;
  courseTitle: string;
  mandatory: boolean;
  active?: boolean;
  scope: RequirementScopeDTO;
  scopeRef?: string | null;
  frequency: RequirementFrequencyDTO;
  anchor: RequirementAnchorDTO;
  fixedDueDate?: string | null;
  graceDays?: number | null;
  recurMonths?: number | null;
  excludedUserIds?: number[];
}

export type RequirementUpdateInput = Partial<RequirementCreateInput>;

/** One cell of the admin compliance matrix (GET /admin/training/compliance). */
export interface ComplianceMatrixRowDTO {
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  participation: ParticipationDTO;
  state: ComplianceStateDTO | null;
  dueSoon: boolean;
  nextDueAt: string | null;
  lastPassedAt: string | null;
}

export interface PaginationDTO {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ComplianceMatrixResponse {
  data: ComplianceMatrixRowDTO[];
  pagination: PaginationDTO;
}

export type ComplianceStatusFilter =
  | 'all'
  | 'compliant'
  | 'not_started'
  | 'overdue'
  | 'due_soon'
  | 'not_required';

export interface ComplianceMatrixParams {
  courseSlug: string;
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ComplianceStatusFilter;
}

// ===== API functions =====

export async function getMyTrainings(): Promise<TrainingStatusDTO[]> {
  const res = await apiGet<{ data: TrainingStatusDTO[] }>('/trainings/my');
  return res.data;
}

/** Read the persisted catalogue (no Dojo call). */
export async function getAvailableCourses(): Promise<CatalogResponse> {
  const res = await apiGet<{ data: AvailableCourseDTO[]; lastSyncAt: string | null }>(
    '/admin/training/available-courses',
  );
  return { courses: res.data, lastSyncAt: res.lastSyncAt };
}

/**
 * "Sync from Dojo": Phase 67 (DJN-04) — enqueues an async catalogue sync
 * (MS→N8N→Dojo→MS) and returns immediately. The catalogue is NOT in the
 * response anymore; it lands later via the inbound receiver, after which the
 * available-courses query reflects the fresh data.
 */
export async function syncCatalog(): Promise<{ queued: boolean }> {
  const res = await apiPost<{ data: { queued: boolean } }>(
    '/admin/training/catalog/sync',
    {},
  );
  return res.data;
}

export async function getTrainingComplianceMatrix(
  params: ComplianceMatrixParams,
): Promise<ComplianceMatrixResponse> {
  const qs = new URLSearchParams({ courseSlug: params.courseSlug });
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.search) qs.set('search', params.search);
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  // The matrix endpoint returns { data, pagination } (no single-level unwrap).
  return apiGet<ComplianceMatrixResponse>(`/admin/training/compliance?${qs.toString()}`);
}

export async function listRequirements(): Promise<RequirementDTO[]> {
  const res = await apiGet<{ data: RequirementDTO[] }>('/admin/training/requirements');
  return res.data;
}

export async function createRequirement(input: RequirementCreateInput): Promise<RequirementDTO> {
  const res = await apiPost<{ data: RequirementDTO }>('/admin/training/requirements', input);
  return res.data;
}

export async function updateRequirement(
  id: number,
  input: RequirementUpdateInput,
): Promise<RequirementDTO> {
  const res = await apiPut<{ data: RequirementDTO }>(`/admin/training/requirements/${id}`, input);
  return res.data;
}

export async function deleteRequirement(id: number): Promise<{ success: boolean }> {
  const res = await apiDelete<{ data: { success: boolean } }>(
    `/admin/training/requirements/${id}`,
  );
  return res.data;
}

// ===== Evidence export (PDF) =====

export type EvidenceVariant = 'customer' | 'audit';

export interface TrainingEvidencePdfParams {
  variant: EvidenceVariant;
  language: 'en' | 'cs';
  /** Omitted = all active mandatory courses. */
  courseSlugs?: string[];
  /** YYYY-MM-DD; counts completions on or after this date. */
  periodFrom?: string;
  /** YYYY-MM-DD; counts completions up to and including this date. */
  periodTo?: string;
  /** Audit variant only. */
  employeeTypeId?: number;
  includeOptional?: boolean;
}

function parseDispositionFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const star = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      /* fall through to the plain filename */
    }
  }
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain?.[1] ?? null;
}

export async function downloadTrainingEvidencePdf(
  params: TrainingEvidencePdfParams,
): Promise<{ blob: Blob; filename: string | null }> {
  const qs = new URLSearchParams();
  qs.set('variant', params.variant);
  qs.set('language', params.language);
  for (const slug of params.courseSlugs ?? []) qs.append('courseSlugs', slug);
  if (params.periodFrom) qs.set('periodFrom', params.periodFrom);
  if (params.periodTo) qs.set('periodTo', params.periodTo);
  if (params.variant === 'audit' && params.employeeTypeId !== undefined) {
    qs.set('employeeTypeId', String(params.employeeTypeId));
  }
  if (params.includeOptional !== undefined) {
    qs.set('includeOptional', String(params.includeOptional));
  }

  const response = await fetch(`${API_BASE_URL}/admin/training/evidence.pdf?${qs.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: getBaseAuthHeaders(),
  });

  if (!response.ok) {
    let message = `Failed to download evidence PDF: ${response.statusText}`;
    try {
      const body = (await response.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }

  return {
    blob: await response.blob(),
    filename: parseDispositionFilename(response.headers.get('Content-Disposition')),
  };
}
